import { Timer, Zap, Hourglass } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useMetricsOverview } from '@/services/admin/metrics.service'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

/**
 * Tempo médio de conclusão de cursos (em dias) + mais rápido / mais lento.
 * Dado vem do `useMetricsOverview.completionTime` (já existia).
 */
export function CompletionTimeCard({ filters }: Props) {
  const { data, isLoading } = useMetricsOverview(filters.courseId, filters.companyIds)

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    )
  }

  const ct = data?.completionTime

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Timer className="w-4 h-4 text-primary" />
          Tempo de conclusão
        </CardTitle>
        <CardDescription>{ct?.totalCompleted ?? 0} cursos concluídos</CardDescription>
      </CardHeader>
      <CardContent>
        {!ct || ct.totalCompleted === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sem conclusões registradas no filtro.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Big number */}
            <div className="text-center">
              <div className="text-5xl font-bold tabular-nums bg-gradient-to-br from-primary to-amber-400 bg-clip-text text-transparent">
                {ct.averageDays}
              </div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
                dias em média
              </div>
            </div>

            {/* Faster / slower */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/40">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-emerald-400 mb-1">
                  <Zap className="w-3 h-3" />
                  <span className="text-[10px] uppercase tracking-wider">Mais rápido</span>
                </div>
                <div className="text-xl font-semibold tabular-nums">{ct.fastestDays}d</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-amber-400 mb-1">
                  <Hourglass className="w-3 h-3" />
                  <span className="text-[10px] uppercase tracking-wider">Mais lento</span>
                </div>
                <div className="text-xl font-semibold tabular-nums">{ct.slowestDays}d</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
